import React, { useState } from 'react';
import { useNewsletterStatus } from '../hooks/useNewsletterStatusSimple';
import { useTranslation } from '../hooks/useTranslation';

const NewsletterSimpleC: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const {
    isLoading,
    error,
    subscribe,
    clearError,
    isValidEmail,
  } = useNewsletterStatus();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email || !isValidEmail(email)) {
      return;
    }

    const success = await subscribe(email);
    if (success) {
      setShowSuccessMessage(true);
      setEmail(''); // Очищаем поле после успешной подписки
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-32 h-32 bg-purple-500/30 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-500/30 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-blue-500/30 rounded-full blur-xl animate-pulse delay-500"></div>
      </div>

      <div className="container mx-auto max-w-4xl text-center relative z-10">
        {!showSuccessMessage ? (
          <>
            <div className="mb-8">
              <div className="relative inline-block mb-6">
                <svg 
                  className="w-16 h-16 text-purple-400 mx-auto animate-bounce" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z" 
                  />
                </svg>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('newsletter.subscribe_title')}
              </h3>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                {t('newsletter.subscribe_subtitle')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 max-w-md mx-auto mb-6">
                <p className="text-red-400 font-semibold flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
                <button 
                  onClick={clearError}
                  className="text-red-300 hover:text-red-200 text-sm mt-2 underline"
                >
                  {t('newsletter.close_button')}
                </button>
              </div>
            )}

            {/* Subscription Form */}
            <div className="max-w-lg mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="email"
                    placeholder={t('newsletter.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('newsletter.subscribing')}
                    </span>
                  ) : (
                    t('newsletter.subscribe_button')
                  )}
                </button>
              </form>

              <p className="text-gray-400 text-sm mt-4">
                {t('newsletter.privacy_notice')}
              </p>
            </div>
          </>
        ) : (
          /* Stylish Success Message */
          <div className="max-w-2xl mx-auto">
            {/* Success Icon with Animation */}
            <div className="relative mb-8">
              <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="absolute inset-0 w-24 h-24 bg-green-500/50 rounded-full mx-auto animate-ping"></div>
              
              {/* Floating particles */}
              <div className="absolute -top-4 -left-4 w-3 h-3 bg-yellow-400 rounded-full animate-bounce delay-75"></div>
              <div className="absolute -top-2 right-8 w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-150"></div>
              <div className="absolute top-8 -right-6 w-4 h-4 bg-purple-400 rounded-full animate-bounce delay-300"></div>
              <div className="absolute bottom-4 -left-8 w-2 h-2 bg-blue-400 rounded-full animate-bounce delay-500"></div>
            </div>

            {/* Success Message */}
            <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-green-500/30 rounded-2xl p-8 shadow-2xl">
              <h2 className="text-4xl font-bold text-white mb-4">
                {t('newsletter.success_title')}
              </h2>
              
              <p className="text-xl text-green-300 mb-6 leading-relaxed">
                {t('newsletter.success_message')}
              </p>

              <div className="flex items-center justify-center space-x-2 text-gray-300 mb-6">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">{t('newsletter.data_security')}</span>
              </div>

              {/* Decorative elements */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg blur"></div>
                <div className="relative bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <p className="text-gray-300 text-sm">
                    {t('newsletter.tip_message')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterSimpleC; 